'use client';

import qs from 'query-string';
import React, { useCallback, useEffect, useState } from 'react';
import { MdArrowDownward, MdArrowUpward, MdClose } from 'react-icons/md';
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { SafeListing } from '@/app/types';

interface FilterMenuProps {
    onClose?: () => void;
    onMapVisibilityChange?: (mapVisible: boolean) => void;
    listings: SafeListing[];
}

type LengthFilterValue = 'half_day' | 'day_1' | 'day_2' | 'day_plus';

type SortByValue = '' | 'newest' | 'price_asc' | 'price_desc' | 'reviews';

const SORT_OPTIONS_NO_PRICE: { id: SortByValue; label: string }[] = [
    { id: '', label: 'Recommended' },
    { id: 'newest', label: 'Newest' },
    { id: 'reviews', label: 'Reviews' },
];

function togglePriceSort(current: SortByValue): SortByValue {
    if (current === 'price_asc') return 'price_desc';
    return 'price_asc';
}

/** Compact chip style (same as Agency) */
function filterChipClass(active: boolean, extra = ''): string {
    return `cursor-pointer rounded-md border px-2 py-0.5 text-xs font-medium transition-colors ${extra} ${
        active
            ? 'border-cyan-600 bg-cyan-100 text-cyan-900'
            : 'border-gray-200 bg-white text-gray-600 hover:border-cyan-300'
    }`;
}

const sectionTitleClass = 'text-sm font-semibold text-gray-800 mb-2';

const FilterMenu: React.FC<FilterMenuProps> = ({ onClose, onMapVisibilityChange, listings }) => {
    const router = useRouter();
    const pathname = usePathname();
    const params = useSearchParams();
    /** Empty string = All languages */
    const [selectedLanguage, setSelectedLanguage] = useState<string>('');
    const [selectedAgencies, setSelectedAgencies] = useState<string[]>([]);
    const [selectedIncluded, setSelectedIncluded] = useState<string[]>([]);
    const [maxStudents, setMaxStudents] = useState<number | null>(null);
    const [minNumberOfDives, setMinNumberOfDives] = useState<number | null>(null);
    const [automaticApproval, setAutomaticApproval] = useState<boolean | null>(null);
    const [lengthFilter, setLengthFilter] = useState<LengthFilterValue | null>(null);
    const [boat, setBoat] = useState<string | null>(null);
    const [sortBy, setSortBy] = useState<SortByValue>('');
    /** Only listings a diver can book without any certification. */
    const [noCertificationRequired, setNoCertificationRequired] = useState(false);

    const language = params?.get('languages');
    const include = params?.get('included');
    const students = params?.get('students');
    const numberOfDives = params?.get('numberOfDives');
    const approval = params?.get('automaticApproval');
    const lengthUnit = params?.get('lengthUnit');
    const length = params?.get('length');
    const lengthFilterParam = params?.get('lengthFilter');
    const boatshore = params?.get('boat');
    const agencyParam = params?.get('agency');
    const sortByParam = params?.get('sortBy');
    const noCertificationParam = params?.get('noCertificationRequired');

    const languages = [
        "English",
        "Chinese (Traditional)",
        "Spanish",
        "French",
        "Afrikaans",
        "Albanian",
        "Arabic",
        "Armenian",
        "Azerbaijani",
        "Bengali",
        "Bosnian",
        "Bulgarian",
        "Burmese",
        "Catalan",
        "Chinese (Simplified)",
        "Croatian",
        "Czech",
        "Danish",
        "Dutch",
        "Estonian",
        "Finnish",
        "Georgian",
        "German",
        "Greek",
        "Hebrew",
        "Hindi",
        "Hungarian",
        "Icelandic",
        "Indonesian",
        "Irish",
        "Italian",
        "Japanese",
        "Korean",
        "Kurdish",
        "Kyrgyz",
        "Lao",
        "Latvian",
        "Lithuanian",
        "Luxembourgish",
        "Macedonian",
        "Malay",
        "Malayalam",
        "Mongolian",
        "Nepali",
        "Norwegian",
        "Persian",
        "Polish",
        "Portuguese",
        "Punjabi",
        "Romanian",
        "Russian",
        "Samoan",
        "Serbian",
        "Slovak",
        "Slovenian",
        "Somali",
        "Sundanese",
        "Swahili",
        "Swedish",
        "Tagalog",
        "Thai",
        "Turkish",
        "Ukrainian",
        "Vietnamese",
    ];

    const included = [
        'Scuba equipment',
        'Dive computer',
        'Weights/Weight Belt/Tanks',
        'Transportation to the dive center',
        'Nitrox',
        '15L tank',
        'Book/e-Learning',
        'Fees',
        'Lunch',
        'Snacks',
        'Water',
        'Coffee/Tea',
    ];

    /** Compact filter list; backend still matches any agency substring in URL */
    const agencies = ['PADI', 'SSI', 'CMAS', 'SDI', 'RAID'] as const;

    useEffect(() => {
        if (students !== undefined && students !== null) {
            setMaxStudents(parseFloat(students));
        }
    }, [students]);

    useEffect(() => {
        if (numberOfDives !== undefined && numberOfDives !== null) {
            setMinNumberOfDives(parseFloat(numberOfDives));
        }
    }, [numberOfDives]);

    useEffect(() => {
        if (include !== undefined && include !== null) {
            const includedArray = include.split(', ');
            setSelectedIncluded(includedArray);
        }
    }, [include]);

    useEffect(() => {
        if (language !== undefined && language !== null && language !== '') {
            setSelectedLanguage(language.split(', ')[0]?.trim() ?? '');
        } else {
            setSelectedLanguage('');
        }
    }, [language]);

    useEffect(() => {
        if (agencyParam !== undefined && agencyParam !== null && agencyParam !== '') {
            setSelectedAgencies(
                agencyParam.split(', ').map((s) => s.trim()).filter(Boolean)
            );
        } else {
            setSelectedAgencies([]);
        }
    }, [agencyParam]);

    useEffect(() => {
        if (approval !== undefined && approval !== null) {
            const isApproved = approval === 'true';
            setAutomaticApproval(isApproved);
        }
    }, [approval]);

    useEffect(() => {
        const valid: LengthFilterValue[] = ['half_day', 'day_1', 'day_2', 'day_plus'];
        if (lengthFilterParam && valid.includes(lengthFilterParam as LengthFilterValue)) {
            setLengthFilter(lengthFilterParam as LengthFilterValue);
            return;
        }
        if (lengthUnit === 'hours') {
            setLengthFilter('half_day');
            return;
        }
        if (lengthUnit === 'days' && length !== undefined && length !== null) {
            const n = parseInt(String(length), 10);
            if (n === 1) setLengthFilter('day_1');
            else if (n === 2) setLengthFilter('day_2');
            else if (n >= 3) setLengthFilter('day_plus');
            else setLengthFilter(null);
            return;
        }
        setLengthFilter(null);
    }, [lengthFilterParam, lengthUnit, length]);

    useEffect(() => {
        if (boatshore !== undefined && boatshore !== null) {
            setBoat(boatshore);
        }
    }, [boatshore]);

    useEffect(() => {
        const valid = new Set(['newest', 'price_asc', 'price_desc', 'reviews']);
        const raw = sortByParam?.trim() ?? '';
        setSortBy(valid.has(raw) ? (raw as SortByValue) : '');
    }, [sortByParam]);

    useEffect(() => {
        setNoCertificationRequired(noCertificationParam === 'true');
    }, [noCertificationParam]);

    const handleAgencyToggle = (value: string) => {
        setSelectedAgencies((prev) =>
            prev.includes(value) ? prev.filter((a) => a !== value) : [...prev, value]
        );
    };

    const clearAgencyFilters = () => setSelectedAgencies([]);

    const handleLengthFilterChange = (value: LengthFilterValue) => {
        setLengthFilter((prev) => (prev === value ? null : value));
    };

    const handleIncludedChange = (item: string) => {
        const updatedIncluded = selectedIncluded.includes(item)
            ? selectedIncluded.filter((inc) => inc !== item)
            : [...selectedIncluded, item];
        setSelectedIncluded(updatedIncluded);
    };

    const handleMaxStudentsChange = (value: number) => {
        if (maxStudents === value) {
            setMaxStudents(null);
        } else {
            setMaxStudents(value);
        }
    };

    const handleMinDivesChange = (value: number) => {
        if (minNumberOfDives === value) {
            setMinNumberOfDives(null);
        } else {
            setMinNumberOfDives(value);
        }
    };

    const handleAutomaticApprovalChange = (approval: boolean | null) => {
        setAutomaticApproval(prevValue => (prevValue === approval ? null : approval));
    };

    const onSubmit = useCallback(() => {

        let currentQuery = {};

        if (params) {
            currentQuery = qs.parse(params.toString())
        }

        const updatedQuery: any = {
            ...currentQuery,
            students: maxStudents,
            numberOfDives: minNumberOfDives,
            automaticApproval: automaticApproval,
            boat: boat,
        }

        delete updatedQuery.length;
        delete updatedQuery.lengthUnit;
        delete updatedQuery.lengthFilter;

        if (selectedLanguage) {
            updatedQuery.languages = selectedLanguage;
        } else {
            delete updatedQuery.languages;
        }

        if (selectedAgencies.length > 0) {
            updatedQuery.agency = selectedAgencies.join(', ');
        } else {
            delete updatedQuery.agency;
        }

        if (lengthFilter) {
            updatedQuery.lengthFilter = lengthFilter;
        }

        if (selectedIncluded.length > 0) {
            updatedQuery.included = selectedIncluded.join(', ');
        } else {
            delete updatedQuery.included;
        }

        if (maxStudents) {
            updatedQuery.students = maxStudents;
        }

        if (minNumberOfDives) {
            updatedQuery.numberOfDives = minNumberOfDives;
        }

        if (automaticApproval !== null) {
            updatedQuery.automaticApproval = automaticApproval;
        }

        if (boat) {
            updatedQuery.boat = boat;
        }

        if (sortBy) {
            updatedQuery.sortBy = sortBy;
        } else {
            delete updatedQuery.sortBy;
        }

        if (noCertificationRequired) {
            updatedQuery.noCertificationRequired = 'true';
        } else {
            delete updatedQuery.noCertificationRequired;
        }

        const basePath = pathname === '/search' ? '/search' : '/';
        const url = qs.stringifyUrl(
            {
                url: basePath,
                query: updatedQuery,
            },
            { skipNull: true }
        );

        router.push(url);
        router.refresh();
    }, [selectedLanguage, selectedAgencies, selectedIncluded, lengthFilter, maxStudents, minNumberOfDives, automaticApproval, language, include, students, numberOfDives, approval, boat, sortBy, noCertificationRequired, pathname, router, params]);

    /** Strip sidebar filter params; keeps main search (location, date, budget, category, etc.) */
    const clearAllFilters = useCallback(() => {
        let currentQuery: Record<string, unknown> = {};
        if (params) {
            currentQuery = qs.parse(params.toString()) as Record<string, unknown>;
        }
        const strip: string[] = [
            'languages',
            'agency',
            'included',
            'students',
            'numberOfDives',
            'automaticApproval',
            'boat',
            'sortBy',
            'length',
            'lengthUnit',
            'lengthFilter',
            'noCertificationRequired',
        ];
        strip.forEach((k) => {
            delete currentQuery[k];
        });
        delete currentQuery.page;

        const basePath = pathname === '/search' ? '/search' : '/';
        const url = qs.stringifyUrl(
            {
                url: basePath,
                query: currentQuery as qs.StringifiableRecord,
            },
            { skipNull: true }
        );
        router.push(url);
        router.refresh();
    }, [params, pathname, router]);

    return (
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden flex h-full min-h-0 max-h-full flex-col">
            {/* Header */}
            <div className="shrink-0 bg-white border-b border-gray-200 px-4 py-3 z-10">
                <div className="flex justify-between items-center gap-2">
                    <h2 className="text-base font-bold text-gray-800">Filter by</h2>
                    <div className="flex shrink-0 gap-1.5">
                        <button
                            onClick={() => {
                                onSubmit();
                                onClose && onClose();
                            }}
                            className="rounded-md bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-cyan-700"
                        >
                            Apply filters
                        </button>
                        {onClose && (
                            <button 
                                onClick={onClose}
                                className="text-gray-600 hover:text-gray-800 p-2 rounded-full hover:bg-gray-100 transition-colors"
                            >
                                <MdClose size={24} />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Scroll: explicit max-height so flex parents never collapse overflow (mobile + desktop) */}
            <div
                className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-5 space-y-5 filter-scrollbar touch-pan-y"
                style={{
                    maxHeight:
                        'calc(100dvh - var(--nav-fab-offset, 10.5rem) - 6.5rem)',
                }}
            >
                <style jsx global>{`
                    .filter-scrollbar::-webkit-scrollbar {
                        width: 8px;
                    }
                    .filter-scrollbar::-webkit-scrollbar-track {
                        background: #f1f5f9;
                        border-radius: 10px;
                    }
                    .filter-scrollbar::-webkit-scrollbar-thumb {
                        background: #cbd5e1;
                        border-radius: 10px;
                        border: 2px solid #f1f5f9;
                    }
                    .filter-scrollbar::-webkit-scrollbar-thumb:hover {
                        background: #94a3b8;
                    }
                    .filter-scrollbar {
                        scrollbar-width: thin;
                        scrollbar-color: #cbd5e1 #f1f5f9;
                    }
                    @media (min-width: 768px) {
                        .filter-scrollbar::-webkit-scrollbar {
                            width: 10px;
                        }
                        .filter-scrollbar div::-webkit-scrollbar {
                            width: 8px;
                        }
                        .filter-scrollbar div::-webkit-scrollbar-track {
                            background: #f1f5f9;
                            border-radius: 10px;
                        }
                        .filter-scrollbar div::-webkit-scrollbar-thumb {
                            background: #cbd5e1;
                            border-radius: 10px;
                            border: 2px solid #f1f5f9;
                        }
                        .filter-scrollbar div::-webkit-scrollbar-thumb:hover {
                            background: #94a3b8;
                        }
                    }
                `}</style>

                {/* Certification — first: a beginner must find this before anything else */}
                <div className="filter-section">
                    <h3 className={sectionTitleClass}>Certification</h3>
                    <button
                        type="button"
                        onClick={() => setNoCertificationRequired((v) => !v)}
                        aria-pressed={noCertificationRequired}
                        className={`flex w-full items-center gap-3 rounded-lg border-2 px-3 py-2.5 text-left transition-colors ${
                            noCertificationRequired
                                ? 'border-green-600 bg-green-50'
                                : 'border-gray-200 bg-white hover:border-green-300'
                        }`}
                    >
                        <span
                            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 ${
                                noCertificationRequired
                                    ? 'border-green-600 bg-green-600 text-white'
                                    : 'border-gray-300 bg-white'
                            }`}
                            aria-hidden
                        >
                            {noCertificationRequired ? '✓' : ''}
                        </span>
                        <span className="text-sm font-bold text-gray-900">
                            No certification required
                        </span>
                    </button>
                </div>

                {/* Sort — first so users see ordering before other filters */}
                <div className="filter-section">
                    <h3 className={sectionTitleClass}>Sort</h3>
                    <div className="flex flex-wrap gap-1.5 items-center">
                        {SORT_OPTIONS_NO_PRICE.map((opt) => {
                            const active = sortBy === opt.id;
                            return (
                                <button
                                    key={opt.id || 'recommended'}
                                    type="button"
                                    onClick={() => setSortBy(opt.id)}
                                    className={filterChipClass(active, '')}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                        <button
                            type="button"
                            onClick={() =>
                                setSortBy((prev) =>
                                    prev === 'price_asc' || prev === 'price_desc'
                                        ? togglePriceSort(prev)
                                        : 'price_asc'
                                )
                            }
                            className={filterChipClass(
                                sortBy === 'price_asc' || sortBy === 'price_desc',
                                'inline-flex items-center gap-1'
                            )}
                            title={
                                sortBy === 'price_desc'
                                    ? 'Price: high to low — tap to show low to high'
                                    : sortBy === 'price_asc'
                                      ? 'Price: low to high — tap to show high to low'
                                      : 'Sort by price'
                            }
                        >
                            <span>Price</span>
                            <span
                                className="inline-flex items-center gap-px"
                                aria-hidden
                            >
                                <MdArrowUpward
                                    className={`h-3.5 w-3.5 shrink-0 ${
                                        sortBy === 'price_asc'
                                            ? 'text-cyan-800'
                                            : sortBy === 'price_desc'
                                              ? 'text-gray-300'
                                              : 'text-gray-400'
                                    }`}
                                />
                                <MdArrowDownward
                                    className={`h-3.5 w-3.5 shrink-0 ${
                                        sortBy === 'price_desc'
                                            ? 'text-cyan-800'
                                            : sortBy === 'price_asc'
                                              ? 'text-gray-300'
                                              : 'text-gray-400'
                                    }`}
                                />
                            </span>
                        </button>
                    </div>
                </div>
                
                {/* Languages */}
                <div className="filter-section">
                    <h3 className={sectionTitleClass}>Languages</h3>
                    <select
                        value={selectedLanguage}
                        onChange={(e) => setSelectedLanguage(e.target.value)}
                        className="w-full rounded-md border border-gray-200 bg-white px-2 py-0.5 text-xs font-medium text-gray-800 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/30"
                    >
                        <option value="">All</option>
                        {languages.map((lang) => (
                            <option key={lang} value={lang}>
                                {lang}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Agency */}
                <div className="filter-section">
                    <h3 className={sectionTitleClass}>Agency</h3>
                    <div className="flex flex-wrap gap-1.5">
                        <button
                            type="button"
                            onClick={clearAgencyFilters}
                            className={filterChipClass(selectedAgencies.length === 0, '')}
                        >
                            All
                        </button>
                        {agencies.map((agency) => {
                            const on = selectedAgencies.includes(agency);
                            return (
                                <label key={agency} className={filterChipClass(on, '')}>
                                    <input
                                        type="checkbox"
                                        className="sr-only"
                                        checked={on}
                                        onChange={() => handleAgencyToggle(agency)}
                                    />
                                    {agency}
                                </label>
                            );
                        })}
                    </div>
                </div>

                {/* Max Students */}
                <div className="filter-section">
                    <h3 className={sectionTitleClass}>Max Students/Instructor</h3>
                    <div className="flex flex-wrap gap-1.5">
                        {[1, 2, 3, 4].map((num) => (
                            <label key={num} className={filterChipClass(maxStudents === num, '')}>
                                <input
                                    type="checkbox"
                                    id={`max-students-${num}`}
                                    checked={maxStudents === num}
                                    onChange={() => handleMaxStudentsChange(num)}
                                    className="sr-only"
                                />
                                {num}
                            </label>
                        ))}
                    </div>
                </div>

                {/* Automatic Approval */}
                <div className="filter-section">
                    <h3 className={sectionTitleClass}>Automatic Approval</h3>
                    <div className="flex flex-wrap gap-1.5">
                        <label className={filterChipClass(automaticApproval === true, '')}>
                            <input
                                type="checkbox"
                                id="automatic-approval"
                                checked={automaticApproval === true}
                                onChange={() => handleAutomaticApprovalChange(true)}
                                className="sr-only"
                            />
                            Yes
                        </label>
                        <label className={filterChipClass(automaticApproval === false, '')}>
                            <input
                                type="checkbox"
                                id="automatic-approval-no"
                                checked={automaticApproval === false}
                                onChange={() => handleAutomaticApprovalChange(false)}
                                className="sr-only"
                            />
                            No
                        </label>
                    </div>
                </div>

                {/* Length */}
                <div className="filter-section">
                    <h3 className={sectionTitleClass}>Length</h3>
                    <div className="flex flex-wrap gap-1.5">
                        {(
                            [
                                { id: 'half_day', label: 'Half day' },
                                { id: 'day_1', label: '1 day' },
                                { id: 'day_2', label: '2 days' },
                                { id: 'day_plus', label: '+2 days' },
                            ] as const
                        ).map((option) => {
                            const v = option.id;
                            const isChecked = lengthFilter === v;
                            return (
                                <label key={option.id} className={filterChipClass(isChecked, 'text-center')}>
                                    <input
                                        type="checkbox"
                                        className="sr-only"
                                        checked={isChecked}
                                        onChange={() => handleLengthFilterChange(v)}
                                    />
                                    {option.label}
                                </label>
                            );
                        })}
                    </div>
                </div>

                {/* Number of dives */}
                <div className="filter-section">
                    <h3 className={sectionTitleClass}>Number of dives</h3>
                    <div className="flex flex-wrap gap-1.5">
                        {[1, 2, 3, 4, '5+'].map((num) => (
                            <label
                                key={num}
                                className={filterChipClass(
                                    minNumberOfDives === (num === '5+' ? 5 : num),
                                    ''
                                )}
                            >
                                <input
                                    type="checkbox"
                                    id={`number-of-dives-${num}`}
                                    checked={minNumberOfDives === (num === '5+' ? 5 : num)}
                                    onChange={() => handleMinDivesChange(num === '5+' ? 5 : Number(num))}
                                    className="sr-only"
                                />
                                {num}
                            </label>
                        ))}
                    </div>
                </div>

                {/* Boat or shore */}
                <div className="filter-section">
                    <h3 className={sectionTitleClass}>Boat or shore dives</h3>
                    <div className="flex flex-wrap gap-1.5">
                        <button
                            type="button"
                            onClick={() => setBoat(null)}
                            className={filterChipClass(boat === null, '')}
                        >
                            All
                        </button>
                        {['Boat', 'Shore', 'Both'].map((option) => (
                            <label key={option} className={filterChipClass(boat === option, '')}>
                                <input
                                    type="radio"
                                    id={option.toLowerCase()}
                                    checked={boat === option}
                                    onChange={() => setBoat(option)}
                                    className="sr-only"
                                />
                                {option}
                            </label>
                        ))}
                    </div>
                </div>

                {/* Included */}
                <div className="filter-section">
                    <h3 className={sectionTitleClass}>Included</h3>
                    <div className="flex flex-wrap gap-1.5">
                        {included.map((item, index) => (
                            <label
                                key={item}
                                className={filterChipClass(selectedIncluded.includes(item), '')}
                            >
                                <input
                                    type="checkbox"
                                    id={`included-${index}`}
                                    checked={selectedIncluded.includes(item)}
                                    onChange={() => handleIncludedChange(item)}
                                    className="sr-only"
                                />
                                {item}
                            </label>
                        ))}
                    </div>
                </div>

                <div className="pt-3 mt-2 flex justify-start border-t border-gray-200">
                    <button
                        type="button"
                        onClick={clearAllFilters}
                        className="rounded-md border border-red-400/25 bg-red-500/[0.08] px-2 py-1 text-[10px] font-medium text-red-800/85 transition-colors hover:border-red-400/40 hover:bg-red-500/[0.12]"
                    >
                        Clear all filters
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FilterMenu;

